/*
  The words of Product settings, taken from the prototype's master-data screen where it has
  them (screens/master-data.html: the tabs, the card titles, "Add colour", the field labels,
  the enabled note on the size type drawer) and written in the same voice where it does not:
  the prototype has no deactivation, no refusal and no moving of colours. One note the
  prototype carries on the colour drawer was copied there from Users ("Disabled users keep
  their history but cannot sign in") and is not used.
*/
export const COPY = {
    eyebrow: 'Products',
    title: 'Product settings',
    tabs: {
        categories: 'Categories',
        brands: 'Brands',
        colours: 'Colours',
        'colour-groups': 'Colour groups',
        sizes: 'Sizes',
        'sale-sources': 'Sale sources',
        'commodity-codes': 'Commodity codes',
        'xero-accounts': 'Xero accounts',
    },
    enabledNote: 'Disabled entries stay on history but cannot be picked on new records.',
    /** Above a taxonomy register for anyone but an Admin, who alone may change it (ADR 0102). */
    adminOnly: {
        title: 'View only',
        body: 'Only an Admin can change categories, colours, sizes and sale sources.',
    },
    status: {
        enabled: 'Enabled',
        disabled: 'Disabled',
        active: 'Active',
        deactivated: 'Deactivated',
    },
    statusFilter: {
        label: 'Status',
        active: 'All active',
        enabled: 'Enabled only',
        disabled: 'Disabled only',
        deactivated: 'Deactivated',
        all: 'Everything',
    },
    columns: {
        created: 'Created',
        revised: 'Revised',
        enabled: 'Enabled',
        active: 'Active',
        /** The phone's label for the Enabled column, so it does not read "Enabled Enabled" over a badge. */
        status: 'Status',
        orderNo: 'Order no',
        code: 'Code',
        products: 'Products',
        country: 'Country',
    },
    products: (n: number) => (n === 1 ? '1 product' : `${n.toLocaleString('en-GB')} products`),
    /** The register's history section, where a record keeps one. */
    history: {
        heading: 'History',
        empty: 'No changes recorded yet.',
        failed: 'The history could not be loaded.',
        actions: {
            Created: 'Added',
            Updated: 'Changed',
            Deactivated: 'Deactivated',
            Reactivated: 'Reactivated',
        } as Record<string, string>,
        by: (who: string | null, when: string) => (who ? `${who}, ${when}` : when),
    },
    /** The confirm before deactivating something that records still carry. */
    inUse: {
        title: (name: string) => `Deactivate ${name}?`,
        body: (count: string, records: string) =>
            `${count} ${records} carry it and keep it. It only leaves the picker for new records. You can bring it back.`,
        bodyUnused: 'It leaves the picker for new records. You can bring it back.',
        confirm: 'Deactivate',
        cancel: 'Keep it',
    },
    audit: {
        never: '—',
        createdBy: (who: string | null, when: string) =>
            who ? `Created by ${who}, ${when}` : `Created ${when}`,
        revisedBy: (who: string | null, when: string) =>
            who ? `Revised by ${who}, ${when}` : `Revised ${when}`,
    },
    drawer: {
        save: 'Save',
        cancel: 'Cancel',
        required: 'Required.',
        deactivate: 'Deactivate',
        reactivate: 'Reactivate',
        /** The drawer section that holds Deactivate or Reactivate. */
        availability: 'Availability',
        orderNo: 'Order no',
        orderNoNote: 'Where it sorts in the app. Zero means the order it was added in.',
        orderNoInvalid: 'A whole number, zero or more.',
    },
    categories: {
        cardTitle: 'Categories',
        add: 'Add category',
        addTitle: 'Add category',
        editTitle: 'Edit category',
        addSecondary: 'Add secondary category',
        addSecondaryTitle: (primary: string) => `Add secondary category · ${primary}`,
        editSecondaryTitle: 'Edit secondary category',
        addTertiary: 'Add tertiary',
        addTertiaryTitle: (secondary: string) => `Add tertiary category · ${secondary}`,
        editTertiaryTitle: 'Edit tertiary category',
        searchLabel: 'Search categories by name or code',
        column: 'Primary category',
        structureColumn: 'Structure',
        structure: (secondaries: number, tertiaries: number) =>
            `${secondaries} secondary · ${tertiaries} tertiary`,
        noSecondaries: 'No secondary categories yet.',
        noTertiaries: 'No tertiary categories',
        hiddenInApp: 'Hidden in app',
        service: 'Service',
        groupFilterAll: 'All groups',
        groupFilterLabel: 'Category group',
        noGroup: 'No group',
        expand: (name: string) => `Expand ${name}`,
        collapse: (name: string) => `Collapse ${name}`,
        editSecondary: (name: string) => `Edit ${name}`,
        editTertiary: (name: string) => `Edit ${name}`,
        fields: {
            name: 'Name',
            code: 'Code',
            codeNote: 'Short code used in product references.',
            secondaryCodeNote: 'Unprefixed, as in the current system (DRS, not CLT-DRS).',
            group: 'Category group',
            groupPlaceholder: 'Choose a category group',
            orderNoNote: 'Sort position in app and portal lists. 0 sorts alphabetically.',
            isService: 'Is service',
            isServiceNote: 'Service categories (styling, sourcing) skip stock and shipping.',
            showInApp: 'Show in app',
            showInAppNote:
                'Hidden secondaries stay usable in the portal but never surface to clients.',
            enabled: 'Enabled',
            enabledNote: 'Disabled entries stay on history but cannot be picked on new records.',
        },
        saved: (name: string) => `Saved · ${name}`,
        added: (name: string) => `Added · ${name}`,
        deactivated: (name: string) => `${name} deactivated`,
        reactivated: (name: string) => `${name} reactivated`,
        deactivateDialog: {
            title: (name: string) => `Deactivate ${name}?`,
            body: (secondaries: number, tertiaries: number) =>
                secondaries === 0
                    ? 'It leaves every picker, in the admin and in the app, and stays on the products that already have it. You can bring it back.'
                    : `Its ${secondaries === 1 ? 'secondary category goes' : `${secondaries} secondary categories go`} with it${tertiaries ? `, ${tertiaries === 1 ? 'tertiary' : `${tertiaries} tertiaries`} included` : ''}, so nothing is left under a category nobody can pick. Products keep what they have. Bringing it back brings the primary alone; the rest come back one by one.`,
            confirm: 'Deactivate',
            cancel: 'Keep it',
        },
        deactivateSecondaryDialog: {
            title: (name: string) => `Deactivate ${name}?`,
            body: (tertiaries: number) =>
                tertiaries === 0
                    ? 'It leaves every picker and the app, and stays on the products that already have it. You can bring it back.'
                    : `Its ${tertiaries === 1 ? 'tertiary category goes' : `${tertiaries} tertiary categories go`} with it. Products keep what they have. You can bring it back.`,
            confirm: 'Deactivate',
            cancel: 'Keep it',
        },
        deactivateTertiaryDialog: {
            title: (name: string) => `Deactivate ${name}?`,
            body: 'It leaves every picker and stays on the products that already have it. You can bring it back.',
            confirm: 'Deactivate',
            cancel: 'Keep it',
        },
        csv: 'categories',
    },
    brands: {
        cardTitle: 'Brands',
        add: 'Add brand',
        addTitle: 'Add brand',
        editTitle: 'Edit brand',
        searchLabel: 'Search brands by name, code, description or country',
        column: 'Brand',
        descriptionColumn: 'Description',
        noDescription: '—',
        noCountry: '—',
        logoAlt: (name: string) => `${name} logo`,
        fields: {
            name: 'Name',
            code: 'Code',
            description: 'Description',
            descriptionNote: 'Optional.',
            country: 'Country',
            countryPlaceholder: 'Choose a country',
            countrySearch: 'Search countries…',
            countryNone: 'No country matches.',
            countryClear: 'No country',
            logo: 'Logo',
            logoDrop: 'Drop an image or click',
            logoFormats: 'PNG, JPEG, WebP or GIF, up to 8 MB',
            logoNote:
                'Resized and compressed before it is stored; shown in the register and on the brand.',
            logoCurrent: 'Current logo',
            logoReplace: 'Replace',
            logoRemove: 'Remove logo',
            logoUploading: 'Saving…',
            logoPending: 'Saved with the brand.',
            logoUploaded: 'Saved.',
            logoStored: 'Shown in the register and on the brand.',
            logoRemoved: 'Logo removed',
            logoTooLarge: 'The image is too large. Up to 8 MB.',
            logoNotImage: 'Upload a PNG, JPEG, WebP or GIF image.',
            logoFailed: 'The logo could not be saved. Try again.',
            logoNotSaved: (reason: string) =>
                `Logo not saved: ${reason} Open the brand to try again.`,
        },
        saved: (name: string) => `Saved · ${name}`,
        added: (name: string) => `Added · ${name}`,
        deactivated: (name: string) => `${name} deactivated`,
        reactivated: (name: string) => `${name} reactivated`,
        deactivateDialog: {
            title: (name: string) => `Deactivate ${name}?`,
            body: (products: number) =>
                products === 0
                    ? 'It leaves every picker, in the admin and in the app. You can bring it back.'
                    : `${products === 1 ? '1 product carries' : `${products.toLocaleString('en-GB')} products carry`} it and keep it. It only leaves the picker for new products. You can bring it back.`,
            confirm: 'Deactivate',
            cancel: 'Keep it',
        },
        csv: 'brands',
    },
    saleSources: {
        cardTitle: 'Sale sources',
        add: 'Add sale source',
        addTitle: 'Add sale source',
        editTitle: 'Edit sale source',
        searchLabel: 'Search sale sources by name',
        column: 'Name',
        ordersColumn: 'Orders',
        orders: (n: number) => (n === 1 ? '1 order' : `${n.toLocaleString('en-GB')} orders`),
        note: 'Every order names one sale source; reports grouped by channel read this list.',
        fields: {
            name: 'Name',
        },
        saved: (name: string) => `Saved · ${name}`,
        added: (name: string) => `Added · ${name}`,
        deactivated: (name: string) => `${name} deactivated`,
        reactivated: (name: string) => `${name} reactivated`,
        records: 'orders',
        csv: 'sale-sources',
    },
    commodityCodes: {
        cardTitle: 'Commodity codes',
        add: 'Add commodity code',
        addTitle: 'Add commodity code',
        editTitle: 'Edit commodity code',
        searchLabel: 'Search commodity codes by number or description',
        column: 'Code',
        descriptionColumn: 'Description',
        countryColumn: 'Default country',
        usedColumn: 'Used',
        used: (n: number) =>
            n === 1 ? 'Used by 1 product' : `Used by ${n.toLocaleString('en-GB')} products`,
        unused: 'Not used yet',
        noCountry: '—',
        fields: {
            code: 'Code (HS)',
            codeNote: '6 to 10 digits, dot-separated.',
            codeInvalid: '6 to 10 digits, dot-separated if you like: 6204.49.00.',
            description: 'Description',
            country: 'Default country of manufacture',
            countryPlaceholder: 'Choose a country',
            countrySearch: 'Search countries…',
            countryNone: 'No country matches.',
            countryClear: 'No country',
        },
        saved: (code: string) => `Saved · ${code}`,
        added: (code: string) => `Added · ${code}`,
        deactivated: (code: string) => `${code} deactivated`,
        reactivated: (code: string) => `${code} reactivated`,
        records: 'products',
        csv: 'commodity-codes',
    },
    colours: {
        cardTitle: 'Colours',
        add: 'Add colour',
        addTitle: 'Add colour',
        editTitle: 'Edit colour',
        searchLabel: 'Search colours by name or group',
        column: 'Colour',
        groupColumn: 'Colour group',
        /** The phone's stacked label, where two words wrap. */
        groupLabelShort: 'Group',
        noGroup: 'No group',
        groupFilterAll: 'All groups',
        groupFilterLabel: 'Colour group',
        fields: {
            name: 'Colour',
            group: 'Colour group',
            groupPlaceholder: 'Choose a colour group',
            groupNote:
                'Sets the swatch shown in lists. The swatch belongs to the group, not the colour.',
            enabled: 'Enabled',
        },
        saved: (name: string) => `Saved · ${name}`,
        added: (name: string) => `Added · ${name}`,
        deactivated: (name: string) => `${name} deactivated`,
        reactivated: (name: string) => `${name} reactivated`,
        deactivateDialog: {
            title: (name: string) => `Deactivate ${name}?`,
            body: 'It leaves every picker, in the admin and in the app, and stays on the orders and products that already have it. You can bring it back.',
            confirm: 'Deactivate',
            cancel: 'Keep it',
        },
        csv: 'colours',
    },
    colourGroups: {
        cardTitle: 'Colour groups',
        add: 'Add colour group',
        addTitle: 'Add colour group',
        editTitle: 'Edit colour group',
        searchLabel: 'Search colour groups by name or code',
        column: 'Group',
        codeColumn: 'Code',
        coloursColumn: 'Colours',
        colours: (n: number) => (n === 1 ? '1 colour' : `${n} colours`),
        fields: {
            name: 'Name',
            code: 'Code',
            codeNote: 'Up to 10 characters.',
            swatch: 'Swatch colour',
            swatchNote:
                'Shown next to every colour in the group, here only. The app keeps its own image for the group.',
            swatchClear: 'No swatch',
        },
        saved: (name: string) => `Saved · ${name}`,
        added: (name: string) => `Added · ${name}`,
        deactivated: (name: string) => `${name} deactivated`,
        reactivated: (name: string) => `${name} reactivated`,
        deactivateDialog: {
            title: (name: string) => `Deactivate ${name}?`,
            body: 'It leaves the group pickers and the app’s colour filter, and stays on everything that already has it. You can bring it back.',
            confirm: 'Deactivate',
            cancel: 'Keep it',
        },
        move: {
            title: (n: number) =>
                n === 1 ? 'Where does its colour go?' : `Where do its ${n} colours go?`,
            body: 'Every colour needs a group. Choose the group they move to; they all move at once and each colour’s history says why.',
            picker: 'New colour group',
            pickerPlaceholder: 'Choose a colour group',
            confirm: 'Move and deactivate',
            cancel: 'Cancel',
            more: (n: number) => `and ${n} more`,
        },
        csv: 'colour-groups',
    },
    sizes: {
        cardTitle: 'Sizes',
        add: 'Add size type',
        addTitle: 'Add size type',
        editTitle: 'Edit size type',
        addSize: 'Add size',
        addSizeTitle: (type: string) => `Add size to ${type}`,
        editSizeTitle: 'Edit size',
        searchLabel: 'Search size types and sizes',
        column: 'Size type',
        sizesColumn: 'Sizes',
        count: (n: number) => (n === 1 ? '1 size' : `${n} sizes`),
        /* The head names only the exceptions, so a type of enabled sizes says its count alone. */
        disabledCount: (n: number) => `${n} disabled`,
        deactivatedCount: (n: number) => `${n} deactivated`,
        noSizes: 'No sizes yet.',
        expand: (name: string) => `Expand ${name}`,
        collapse: (name: string) => `Collapse ${name}`,
        editSize: (name: string) => `Edit ${name}`,
        /** A chip that is not simply enabled says so in its name, as it does on its face. */
        editSizeStatus: (name: string, status: string) => `Edit ${name}, ${status.toLowerCase()}`,
        find: 'Find a size',
        findLabel: (type: string) => `Find a size in ${type}`,
        noMatch: (term: string) => `No size matches \u201c${term}\u201d`,
        delete: 'Delete',
        duplicate: 'This size type already has that size.',
        fields: {
            typeName: 'Size type',
            sizeName: 'Size',
            enabled: 'Enabled',
        },
        saved: (name: string) => `Saved · ${name}`,
        added: (name: string) => `Added · ${name}`,
        deactivated: (name: string) => `${name} deactivated`,
        reactivated: (name: string) => `${name} reactivated`,
        sizeDeleted: (name: string) => `${name} deleted`,
        deactivateDialog: {
            title: (name: string) => `Deactivate ${name}?`,
            body: 'The type and its sizes leave every picker and stay on the orders that already have them. Reactivating brings the lot back.',
            confirm: 'Deactivate',
            cancel: 'Keep it',
        },
        csv: 'sizes',
    },
    /*
      The Xero accounts register (ALS-386). No frame draws it, so the words are ours, in the
      register's voice: the chart is read from Xero, never typed, and the one thing the team sets
      is what a product or a line may take.
    */
    xeroAccounts: {
        cardTitle: 'Xero accounts',
        searchLabel: 'Search accounts by code or name',
        refresh: 'Refresh from Xero',
        refreshed: (count: number) => `Read ${count.toLocaleString('en-GB')} accounts from Xero`,
        refreshFailed: 'The chart of accounts was not read',
        read: (when: string, organisation: string | null) =>
            organisation ? `Read ${when} from ${organisation}` : `Read ${when}`,
        notRead: {
            title: 'Xero’s chart of accounts has not been read here yet',
            body: 'Until it is, products and order lines offer the accounts the old portal holds.',
            refresh: 'Refresh from Xero to read it.',
            notLinked: 'Connect Xero first, from the Xero badge on the Orders page.',
            notAdmin: 'An administrator can read it from Xero.',
        },
        notLinked: 'Xero is not connected, so the chart cannot be read.',
        showLabel: 'Show',
        show: { sales: 'Sales accounts', all: 'Whole chart' },
        columns: {
            code: 'Code',
            name: 'Name',
            type: 'Type',
            status: 'In Xero',
            use: 'On lines',
        },
        status: { Active: 'Active', Archived: 'Archived', Gone: 'Not in Xero' },
        use: { Daily: 'Daily', Offered: 'Offered', Hidden: 'Hidden' },
        lineUseLabel: (account: string) => `On lines: ${account}`,
        lineUseNote:
            'Daily accounts come first in every account select; hidden ones are not offered.',
        lineUseSaved: (account: string, use: string) => `${account} · ${use}`,
        notForLines: 'Not for lines',
        noCode: 'No code',
        csv: 'xero-accounts',
        checks: {
            title: 'What the admin posts to',
            note: 'Xero refuses an invoice that names an account or a tax type it does not have.',
            chargesHeading: 'Accounts in settings',
            taxesHeading: 'Tax rates',
            charge: {
                Products: 'A product with no account',
                AListFee: 'A-List fee',
                CardFee: 'Card fee',
                Duties: 'Taxes & duties',
                Shipping: 'Shipping',
                WalletCredit: 'Wallet top-up credit notes',
                BankTransferPayments: 'Bank transfers recorded here',
                CardPayments: 'Card payments recorded here',
            },
            state: {
                Ok: 'In Xero',
                Archived: 'Archived in Xero',
                Missing: 'Not in Xero',
                Unmapped: 'No Xero tax type',
                NotRead: 'Not checked',
            },
            none: 'None',
        },
    },
} as const;
